import { isCurrentAssessment, FinancialUnavailable, type FinancialResponseAvailability } from '@/components/financial/financial-unavailable';
import { netRevenue } from '@/lib/financial/revenue';
import { cascade, CalcInputError, fromCents, sumCents, toCents } from '@shared/calc';
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  ArrowLeft,
  Edit,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

interface AccountAnalysisData {
  account: {
    id: string;
    legalName?: string;
    accountName?: string;
    accountNumber?: string;
  };
  entityRecord: {
    id: string;
    financialTemplate: string;
  };
  periods: Array<{
    id: string;
    periodLabel: string;
    periodType: string;
    periodStartDate: string;
    periodEndDate: string;
  }>;
  healthScore: FinancialResponseAvailability & {
    overallScore: number;
    healthRating: string;
    liquidityScore: number;
    leverageScore: number;
    profitabilityScore: number;
    efficiencyScore: number;
    growthScore: number;
    trendDirection?: string;
    previousOverallScore?: number;
  };
  financialSummary: {
    revenue: number;
    revenueChange?: number;
    ebitda: number;
    ebitdaChange?: number;
    netProfit: number;
    netProfitChange?: number;
    totalAssets: number;
    workingCapital: number;
  };
  plTrends: Array<{
    periodId: string;
    periodLabel: string;
    revenue: number;
    ebitda: number;
    netProfit: number | null;
  }>;
  ratios: Record<string, {
    currentRatio?: number | null;
    quickRatio?: number | null;
    debtToEquity?: number | null;
    netDebtToEbitda?: number | null;
    interestCoverageRatio?: number | null;
    ebitdaMargin?: number | null;
    roe?: number | null;
    dso?: number | null;
  }>;
  periodData: {
    pl: Record<string, any[]>;
    bs: Record<string, any[]>;
  };
}

// Health rating badge variants
const healthRatingBadgeClasses: Record<string, string> = {
  excellent: "bg-green-500 hover:bg-green-600 text-white",
  good: "bg-blue-500 hover:bg-blue-600 text-white",
  fair: "bg-yellow-500 hover:bg-yellow-600 text-white",
  poor: "bg-orange-500 hover:bg-orange-600 text-white",
  critical: "bg-red-500 hover:bg-red-600 text-white",
};

// Industry benchmarks for ratio comparison
const industryBenchmarks: Record<string, { value: number; label: string }> = {
  current_ratio: { value: 1.5, label: "Current Ratio" },
  quick_ratio: { value: 1.0, label: "Quick Ratio" },
  debt_to_equity: { value: 1.2, label: "Debt/Equity" },
  ebitda_margin: { value: 15, label: "EBITDA Margin" },
  roe: { value: 12, label: "ROE" },
  interest_coverage: { value: 3.0, label: "Interest Coverage" },
};

function TrendIcon({ change }: { change: number }) {
  if (change > 0)
    return <TrendingUp className="h-4 w-4 text-green-600" />;
  if (change < 0)
    return <TrendingDown className="h-4 w-4 text-red-600" />;
  return <Minus className="h-4 w-4 text-gray-400" />;
}

// Chart colour constants (using Tailwind/shadcn theme values)
const CHART_COLORS = {
  revenue: "#3b82f6",  // blue-500
  ebitda: "#10b981",   // emerald-500
  netProfit: "#8b5cf6", // purple-500
};

export default function AccountAnalysisDashboard() {
  const params = useParams();
  const accountId = params.accountId;
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | undefined>(undefined);

  const { data, isLoading, error } = useQuery<AccountAnalysisData>({
    queryKey: ["/api/dashboard/account", accountId, { ...(selectedPeriodId ? { periodId: selectedPeriodId } : {}) }],
    enabled: !!accountId,
  });

  const formatCurrency = (value: number | null) => {
    if (value === null) return "-";
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    } else {
      return `$${value.toLocaleString()}`;
    }
  };

  const formatPercentage = (value: number) => {
    return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
  };

  // Get account name from data
  const accountName = data?.account?.legalName || data?.account?.accountName || "Account";

  // Check for no financial data
  const hasNoFinancialData = data && !data.entityRecord;
  const hasFinancialData = data?.entityRecord && data?.periods && data.periods.length > 0;

  // Build P&L trends for chart
  const plTrendData = data?.plTrends || [];

  // Build ratio bars data
  const buildRatioBars = () => {
    if (!data?.ratios || !data?.periods || data.periods.length === 0) return [];

    // Get the latest period's ratios
    const latestPeriodId = data.periods[data.periods.length - 1]?.id;
    const latestRatios = latestPeriodId ? data.ratios[latestPeriodId] : null;

    if (!latestRatios) return [];

    const bars = [];

    if (latestRatios.currentRatio != null) {
      bars.push({
        key: "current_ratio",
        label: "Current Ratio",
        value: latestRatios.currentRatio,
        benchmark: industryBenchmarks.current_ratio.value,
        unit: "x",
      });
    }
    if (latestRatios.quickRatio != null) {
      bars.push({
        key: "quick_ratio",
        label: "Quick Ratio",
        value: latestRatios.quickRatio,
        benchmark: industryBenchmarks.quick_ratio.value,
        unit: "x",
      });
    }
    if (latestRatios.debtToEquity != null) {
      bars.push({
        key: "debt_to_equity",
        label: "Debt/Equity",
        value: latestRatios.debtToEquity,
        benchmark: industryBenchmarks.debt_to_equity.value,
        unit: "x",
      });
    }
    if (latestRatios.ebitdaMargin != null) {
      bars.push({
        key: "ebitda_margin",
        label: "EBITDA Margin",
        value: latestRatios.ebitdaMargin,
        benchmark: industryBenchmarks.ebitda_margin.value,
        unit: "%",
      });
    }
    if (latestRatios.interestCoverageRatio != null) {
      bars.push({
        key: "interest_coverage",
        label: "Interest Coverage",
        value: latestRatios.interestCoverageRatio,
        benchmark: industryBenchmarks.interest_coverage.value,
        unit: "x",
      });
    }
    if (latestRatios.roe != null) {
      bars.push({
        key: "roe",
        label: "ROE",
        value: latestRatios.roe,
        benchmark: industryBenchmarks.roe.value,
        unit: "%",
      });
    }

    return bars;
  };

  const ratioBars = buildRatioBars();

  // Build period comparison table data
  const buildPeriodComparison = () => {
    if (!data?.periods || !data?.periodData?.pl) return [];

    return data.periods.map((period, index) => {
      const plItems = data.periodData.pl[period.id] || [];

      const revenue = netRevenue(plItems);
      let expenses: number | null = null;
      if (plItems.length > 0) {
        try {
          // Match the server trend's raw-period basis, including YTD periods.
          const calculated = cascade({
            periodLabel: period.periodLabel, periodType: "historical", monthsInPeriod: 12, fyEndMonth: 6,
            profitLoss: plItems.map(item => ({ lineItem: item.lineItem, amountCents: toCents(item.amount) })),
            balanceSheet: [],
          });
          expenses = fromCents(sumCents([
            calculated.cogsCents, calculated.operatingExpensesCents, calculated.otherExpensesCents,
            calculated.depreciationAmortisationCents, calculated.interestExpenseCents, calculated.taxCents,
          ]));
        } catch (error) {
          if (!(error instanceof CalcInputError)) throw error;
        }
      }
      const netProfit = data.plTrends?.find(trend => trend.periodId === period.id)?.netProfit ?? null;

      // Calculate YoY change if we have a previous period
      let yoyChange: number | null = null;
      if (index > 0) {
        const prevPeriodId = data.periods[index - 1].id;
        const prevPlItems = data.periodData.pl[prevPeriodId] || [];
        const prevRevenue = netRevenue(prevPlItems);
        if (revenue !== null && prevRevenue !== null && prevRevenue > 0) {
          yoyChange = ((revenue - prevRevenue) / prevRevenue) * 100;
        }
      }

      return {
        periodId: period.id,
        label: period.periodLabel,
        revenue,
        expenses,
        profit: netProfit,
        yoyChange,
      };
    });
  };

  const periodComparison = buildPeriodComparison();

  // Error state
  if (error) {
    return (
      <div data-testid="page-account-analysis" className="flex h-screen w-full flex-col">
        <PageHeader
          title="Account Analysis"
          breadcrumbs={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Account Analysis" },
          ]}
        />
        <main className="flex-1 overflow-auto bg-muted/20 p-6">
          <Card>
            <CardContent className="flex items-center justify-center p-12">
              <div className="text-center" data-testid="error-state-invalid-account">
                <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
                <h3 className="mt-4 text-lg font-semibold">
                  {error.message?.includes("not found") ? "Account Not Found" : "Error Loading Data"}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {error.message?.includes("not found")
                    ? "The account you're looking for doesn't exist or has been removed."
                    : error.message || "Unable to load account analysis. Please try again later."}
                </p>
                <Button
                  variant="outline"
                  className="mt-4"
                  asChild
                  data-testid="button-back-to-account"
                >
                  <Link href="/accounts">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Accounts
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Loading state with skeletons
  if (isLoading) {
    return (
      <div data-testid="page-account-analysis" className="flex h-screen w-full flex-col">
        <PageHeader
          title="Account Analysis"
          breadcrumbs={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Account Analysis" },
          ]}
        />
        <main className="flex-1 overflow-auto bg-muted/20 p-6">
          <div className="mx-auto max-w-7xl space-y-6">
            {/* Header skeleton */}
            <div className="flex items-center justify-between">
              <div>
                <Skeleton data-testid="loading-skeleton-header" className="h-8 w-64 mb-2" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-9 w-32" />
                <Skeleton className="h-9 w-32" />
              </div>
            </div>

            {/* Financial summary skeleton */}
            <div data-testid="section-financial-summary">
              <Card>
                <CardHeader>
                  <Skeleton data-testid="loading-skeleton-summary" className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="h-20" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts skeleton */}
            <Card>
              <CardHeader>
                <Skeleton data-testid="loading-skeleton-chart" className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[300px] w-full" />
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Empty state when no financial data
  if (hasNoFinancialData) {
    return (
      <div data-testid="page-account-analysis" className="flex h-screen w-full flex-col">
        <PageHeader
          title="Account Analysis"
          breadcrumbs={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Account Analysis" },
            { label: accountName },
          ]}
        />
        <main className="flex-1 overflow-auto bg-muted/20 p-6">
          <div className="mx-auto max-w-7xl space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">{accountName}</h1>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  asChild
                  data-testid="button-back-to-account"
                >
                  <Link href={`/accounts/${accountId}`}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Account
                  </Link>
                </Button>
                <Button
                  asChild
                  data-testid="button-edit-financials"
                >
                  <Link href={`/accounts/${accountId}?tab=financials`}>
                    <Edit className="mr-2 h-4 w-4" />
                    Add Financial Data
                  </Link>
                </Button>
              </div>
            </div>

            {/* Empty state */}
            <Card data-testid="section-financial-summary">
              <CardContent className="flex flex-col items-center justify-center p-12">
                <div data-testid="empty-state-no-data" className="text-center">
                  <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-semibold">No Financial Data Available</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    This account doesn't have any financial data yet. Add financial data to see analysis.
                  </p>
                  <Button
                    className="mt-4"
                    asChild
                  >
                    <Link href={`/accounts/${accountId}?tab=financials`}>
                      <Edit className="mr-2 h-4 w-4" />
                      Add Financial Data
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="page-account-analysis" className="flex h-screen w-full flex-col">
      <PageHeader
        title="Account Analysis"
        breadcrumbs={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Account Analysis" },
          {
            label: accountName,
            href: `/accounts/${data?.account?.id}`,
          },
        ]}
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{accountName}</h1>
              {data?.account?.accountNumber && (
                <p className="text-sm text-muted-foreground">
                  Account: {data.account.accountNumber}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                asChild
                data-testid="button-back-to-account"
              >
                <Link href={`/accounts/${accountId}`}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Account
                </Link>
              </Button>
              <Button
                asChild
                data-testid="button-edit-financials"
              >
                <Link href={`/accounts/${accountId}?tab=financials`}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Financials
                </Link>
              </Button>
            </div>
          </div>

          {/* Period Selector */}
          {hasFinancialData && (
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">Period:</span>
                  <Select
                    value={selectedPeriodId || data.periods[data.periods.length - 1]?.id || ""}
                    onValueChange={(value) => setSelectedPeriodId(value)}
                  >
                    <SelectTrigger
                      className="w-[200px]"
                      data-testid="dropdown-period-selector"
                    >
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      {data.periods.map((period) => (
                        <SelectItem key={period.id} value={period.id}>
                          {period.periodLabel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Row 1: Financial Summary Section */}
          <div data-testid="section-financial-summary">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Health Score Card */}
              <Card data-testid="card-health-score">
                <CardHeader>
                  <CardTitle>Health Score</CardTitle>
                  <CardDescription>
                    Overall business health assessment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {data?.healthScore && isCurrentAssessment(data.healthScore) && data.healthScore.overallScore != null ? (
                    <div className="flex flex-col items-center">
                      {/* Score gauge */}
                      <div className="relative w-32 h-32 mb-4">
                        <svg className="w-full h-full" viewBox="0 0 100 100">
                          <circle
                            className="text-gray-200 stroke-current"
                            strokeWidth="10"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="transparent"
                          />
                          <circle
                            className={`stroke-current ${
                              data.healthScore.healthRating === "excellent" ? "text-green-500" :
                              data.healthScore.healthRating === "good" ? "text-blue-500" :
                              data.healthScore.healthRating === "fair" ? "text-yellow-500" :
                              data.healthScore.healthRating === "poor" ? "text-orange-500" :
                              "text-red-500"
                            }`}
                            strokeWidth="10"
                            strokeLinecap="round"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="transparent"
                            strokeDasharray={`${((data.healthScore.overallScore || 0) / 100) * 251.2} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-3xl font-bold">
                            {data.healthScore.overallScore}
                          </span>
                        </div>
                      </div>

                      {/* Rating badge */}
                      <Badge
                        data-testid={`badge-rating-${data.healthScore.healthRating}`}
                        className={healthRatingBadgeClasses[data.healthScore.healthRating] || "bg-gray-500 text-white"}
                      >
                        {data.healthScore.healthRating?.toUpperCase()}
                      </Badge>

                      {/* Trend indicator */}
                      {data.healthScore.trendDirection && (
                        <div className="flex items-center gap-1 mt-2 text-sm">
                          {data.healthScore.trendDirection === "improving" && (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          )}
                          {data.healthScore.trendDirection === "declining" && (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                          {data.healthScore.trendDirection === "stable" && (
                            <Minus className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="capitalize">{data.healthScore.trendDirection}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <FinancialUnavailable accountId={accountId} value={data?.healthScore} action="Open financial data and calculate after verification." />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Key Ratios Card */}
              <Card data-testid="card-key-ratios">
                <CardHeader>
                  <CardTitle>Key Ratios</CardTitle>
                  <CardDescription>
                    Latest period performance metrics
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {ratioBars.length > 0 ? (
                    <div className="space-y-3">
                      {ratioBars.slice(0, 4).map((ratio) => (
                        <div key={ratio.key} data-testid={`ratio-item-${ratio.key}`}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{ratio.label}</span>
                            <span className="font-medium">
                              {ratio.value?.toFixed(2)}{ratio.unit}
                            </span>
                          </div>
                          <Progress
                            value={Math.min((ratio.value / (ratio.benchmark * 2)) * 100, 100)}
                            className="h-2"
                          />
                          <div className="text-xs text-muted-foreground mt-1">
                            Benchmark: {ratio.benchmark}{ratio.unit}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      No ratio data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Row 2: Financial Trends Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Financial Trends</CardTitle>
              <CardDescription>
                Revenue, EBITDA, and Net Profit over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div data-testid="chart-financial-trends">
                {plTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={plTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodLabel" />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        name="Revenue"
                        stroke={CHART_COLORS.revenue}
                        strokeWidth={2}
                        data-testid="chart-legend-revenue"
                      />
                      <Line
                        type="monotone"
                        dataKey="ebitda"
                        name="EBITDA"
                        stroke={CHART_COLORS.ebitda}
                        strokeWidth={2}
                        data-testid="chart-legend-ebitda"
                      />
                      <Line
                        type="monotone"
                        dataKey="netProfit"
                        name="Net Profit"
                        stroke={CHART_COLORS.netProfit}
                        strokeWidth={2}
                        data-testid="chart-legend-net-profit"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                    No trend data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Row 3: Ratio Benchmarks Section */}
          <div data-testid="section-ratio-benchmarks">
            <Card>
              <CardHeader>
                <CardTitle>Ratio Benchmarks</CardTitle>
                <CardDescription>
                  Compare actual ratios against industry benchmarks
                </CardDescription>
              </CardHeader>
              <CardContent>
                {ratioBars.length > 0 ? (
                  <div className="space-y-4">
                    {ratioBars.map((ratio) => {
                      const percentOfBenchmark = (ratio.value / ratio.benchmark) * 100;
                      const isAboveBenchmark = ratio.value >= ratio.benchmark;
                      // For debt ratios, lower is better
                      const isDebtRatio = ratio.key === "debt_to_equity";
                      const isGood = isDebtRatio ? !isAboveBenchmark : isAboveBenchmark;

                      return (
                        <div key={ratio.key} data-testid={`ratio-bar-${ratio.key}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">{ratio.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                {ratio.value?.toFixed(2)}{ratio.unit}
                              </span>
                              <Badge
                                data-testid={`benchmark-indicator-${ratio.key}`}
                                variant={isGood ? "default" : "secondary"}
                                className={isGood ? "bg-green-500" : "bg-yellow-500"}
                              >
                                {isAboveBenchmark ? "Above" : "Below"}
                              </Badge>
                            </div>
                          </div>
                          <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                            {/* Benchmark line */}
                            <div
                              className="absolute top-0 bottom-0 w-0.5 bg-gray-600 z-10"
                              style={{ left: "50%" }}
                            />
                            {/* Actual value bar */}
                            <div
                              className={`h-full ${isGood ? "bg-green-500" : "bg-yellow-500"} transition-all`}
                              style={{
                                width: `${Math.min(percentOfBenchmark / 2, 100)}%`,
                              }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>0</span>
                            <span>Benchmark: {ratio.benchmark}{ratio.unit}</span>
                            <span>{(ratio.benchmark * 2).toFixed(1)}{ratio.unit}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No ratio data available for benchmarking
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 4: Period Comparison Table */}
          <div data-testid="section-period-analysis">
            <Card>
              <CardHeader>
                <CardTitle>Period-over-Period Analysis</CardTitle>
                <CardDescription>
                  Compare financial performance across periods
                </CardDescription>
              </CardHeader>
              <CardContent>
                {periodComparison.length > 0 ? (
                  <Table data-testid="table-period-comparison">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Expenses</TableHead>
                        <TableHead className="text-right">Profit</TableHead>
                        <TableHead className="text-right">YoY Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {periodComparison.map((period) => (
                        <TableRow key={period.periodId} data-testid={`row-period-${period.periodId}`}>
                          <TableCell className="font-medium">{period.label}</TableCell>
                          <TableCell className="text-right">{formatCurrency(period.revenue)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(period.expenses)}</TableCell>
                          <TableCell className="text-right">{period.profit === null ? "-" : formatCurrency(period.profit)}</TableCell>
                          <TableCell className="text-right">
                            {period.yoyChange !== null ? (
                              <div className="flex items-center justify-end gap-1">
                                <TrendIcon change={period.yoyChange} />
                                <span className={period.yoyChange >= 0 ? "text-green-600" : "text-red-600"}>
                                  {formatPercentage(period.yoyChange)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No period data available for comparison
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
